import { makeAutoObservable, runInAction } from 'mobx';
import { inviteCodeApi } from '@/services/inviteCodeApi';
import { brandStore } from './brandStore';
import type {
  BetaAccess as ApiBetaAccess,
  GrantBetaAccessRequest,
} from '@/types';
import { BetaAccessStatus } from '@/types';

export interface BetaAccess {
  id: string;
  userId: string;
  grantSource: string;
  grantRefType?: string;
  grantRefId?: string;
  grantedBy?: string;
  grantedAt?: string;
  revokedBy?: string;
  revokedAt?: string;
  updatedAt?: string;
  active: boolean;
  status: 'active' | 'revoked';
}

function convertStatus(
  status: BetaAccessStatus | keyof typeof BetaAccessStatus | string | undefined,
  active?: boolean,
  revokedAt?: string,
): 'active' | 'revoked' {
  const normalizedStatus = String(status);

  if (
    status === BetaAccessStatus.BETA_ACCESS_STATUS_REVOKED ||
    normalizedStatus === 'BETA_ACCESS_STATUS_REVOKED' ||
    normalizedStatus === '2'
  ) {
    return 'revoked';
  }

  if (
    status === BetaAccessStatus.BETA_ACCESS_STATUS_ACTIVE ||
    normalizedStatus === 'BETA_ACCESS_STATUS_ACTIVE' ||
    normalizedStatus === '1'
  ) {
    return 'active';
  }

  if (active === false || Boolean(revokedAt)) {
    return 'revoked';
  }

  return 'active';
}

function transformBetaAccess(apiItem: ApiBetaAccess): BetaAccess {
  return {
    id: apiItem.id,
    userId: apiItem.user_id,
    grantSource: apiItem.grant_source,
    grantRefType: apiItem.grant_ref_type,
    grantRefId: apiItem.grant_ref_id,
    grantedBy: apiItem.granted_by,
    grantedAt: apiItem.granted_at,
    revokedBy: apiItem.revoked_by,
    revokedAt: apiItem.revoked_at,
    updatedAt: apiItem.updated_at,
    active: apiItem.active,
    status: convertStatus(apiItem.status, apiItem.active, apiItem.revoked_at),
  };
}

class BetaAccessStore {
  betaAccesses: BetaAccess[] = [];
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  searchUserId = '';
  statusFilter: BetaAccessStatus | undefined =
    BetaAccessStatus.BETA_ACCESS_STATUS_ACTIVE;
  sourceFilter = '';
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  private ensureUpdatedItemVisible(nextStatus: 'active' | 'revoked') {
    const currentStatusFilter = this.statusFilter;
    const isFilteredOutAfterUpdate =
      (nextStatus === 'revoked' &&
        currentStatusFilter === BetaAccessStatus.BETA_ACCESS_STATUS_ACTIVE) ||
      (nextStatus === 'active' &&
        currentStatusFilter === BetaAccessStatus.BETA_ACCESS_STATUS_REVOKED);

    if (isFilteredOutAfterUpdate) {
      this.statusFilter = undefined;
      this.pagination.page = 1;
    }

    return isFilteredOutAfterUpdate;
  }

  private async fetchBetaAccessesForApp(appId: string) {
    const response = await inviteCodeApi.listBetaAccesses({
      app_id: appId,
      page_num: this.pagination.page,
      page_size: this.pagination.pageSize,
      user_id: this.searchUserId || undefined,
      status: this.statusFilter,
      grant_source: this.sourceFilter || undefined,
    });

    runInAction(() => {
      this.betaAccesses = response.beta_accesses.map(transformBetaAccess);
      this.pagination.total = response.total;
      this.loading = false;
    });
  }

  async fetchBetaAccesses() {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await this.fetchBetaAccessesForApp(appId);
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async grantBetaAccess(data: GrantBetaAccessRequest) {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await inviteCodeApi.grantBetaAccess({
        ...data,
        app_id: appId,
      });
      this.ensureUpdatedItemVisible('active');
      await this.fetchBetaAccessesForApp(appId);
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async revokeBetaAccess(userId: string) {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await inviteCodeApi.revokeBetaAccess({ user_id: userId, app_id: appId });
      this.ensureUpdatedItemVisible('revoked');
      await this.fetchBetaAccessesForApp(appId);
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  setSearchUserId(userId: string) {
    this.searchUserId = userId;
    this.pagination.page = 1;
  }

  setStatusFilter(status: BetaAccessStatus | undefined) {
    this.statusFilter = status;
    this.pagination.page = 1;
  }

  setSourceFilter(source: string) {
    this.sourceFilter = source;
    this.pagination.page = 1;
  }

  setPagination(page: number, pageSize?: number) {
    this.pagination.page = page;
    if (pageSize !== undefined) {
      this.pagination.pageSize = pageSize;
    }
  }
}

export const betaAccessStore = new BetaAccessStore();
